import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Segment,
  Header,
  Message,
  Form,
  Loader,
  Dimmer,
  Label,
  List,
} from "semantic-ui-react";
import { fetchUserAttributes } from "@aws-amplify/auth";
import axios from "../../../apis/dvdlocator";

interface ListingChannel {
  listing_id: string;
  msku: string | null;
  listing_title: string | null;
  fulfillment_channel: string | null;
}

interface AsinGroup {
  asin: string;
  created_by: string | null;
  created_at: string | null;
  listings: ListingChannel[];
}

interface ProductSearchResult {
  product_id: number;
  product_title: string;
}

interface CreateProductListingProps {
  productId?: number;
  productTitle?: string;
  onClose: (didCreate?: boolean) => void;
}

function getOffersLabel(listings: ListingChannel[]): string {
  const hasFba = listings.some((l) => l.fulfillment_channel === "AMAZON_NA");
  const hasMerchant = listings.some(
    (l) => l.fulfillment_channel && l.fulfillment_channel !== "AMAZON_NA"
  );
  const labels = [hasMerchant && "Merchant", hasFba && "FBA"].filter(Boolean);
  return labels.length > 0 ? labels.join(" & ") : "—";
}

function getDisplayTitle(listings: ListingChannel[]): string {
  const fbaListing = listings.find(
    (l) => l.fulfillment_channel === "AMAZON_NA" && l.listing_title
  );
  if (fbaListing) return fbaListing.listing_title as string;
  const anyListing = listings.find((l) => l.listing_title);
  return anyListing?.listing_title ?? "—";
}

export function CreateProductListing({
  productId,
  productTitle,
  onClose,
}: CreateProductListingProps) {
  // Product search state (used when no productId prop is provided)
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<
    number | undefined
  >(undefined);
  const [selectedProductTitle, setSelectedProductTitle] = useState("");

  const effectiveProductId = productId ?? selectedProductId;
  const effectiveProductTitle = productTitle || selectedProductTitle;

  const needsSearch = productId === undefined;

  // Debounce the search query
  useEffect(() => {
    if (!needsSearch) return;
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery, needsSearch]);

  // Fetch search results
  useEffect(() => {
    if (!needsSearch || !debouncedQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const abortController = new AbortController();
    async function fetchResults() {
      setIsSearching(true);
      try {
        const res = await axios.get("/products", {
          params: { search: debouncedQuery.trim() },
          signal: abortController.signal,
        });
        setSearchResults(res.data ?? []);
      } catch {
        if (!abortController.signal.aborted) setSearchResults([]);
      } finally {
        if (!abortController.signal.aborted) setIsSearching(false);
      }
    }
    fetchResults();
    return () => abortController.abort();
  }, [debouncedQuery, needsSearch]);

  // Existing listings state
  const [asinGroups, setAsinGroups] = useState<AsinGroup[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createdBy, setCreatedBy] = useState("");
  const [createdOn] = useState(() => new Date().toLocaleString());

  useEffect(() => {
    fetchUserAttributes()
      .then((attrs) => {
        if (attrs.name) setCreatedBy(attrs.name);
      })
      .catch((err) => console.log(err));
  }, []);

  const [asin, setAsin] = useState("");
  const [msku, setMsku] = useState("");
  const [listingTitle, setListingTitle] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!effectiveProductId) return;
    const abortController = new AbortController();

    async function fetchListings() {
      setLoadingListings(true);
      setLoadError(null);
      try {
        const res = await axios.get(`/product/${effectiveProductId}/listings`, {
          signal: abortController.signal,
        });
        setAsinGroups(res.data.asins ?? []);
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          setLoadError(
            err.response?.data?.message ||
              "Failed to load existing listings. Please close and try again."
          );
        }
      } finally {
        if (!abortController.signal.aborted) setLoadingListings(false);
      }
    }

    fetchListings();
    return () => abortController.abort();
  }, [effectiveProductId]);

  const asinTrimmed = asin.trim().toUpperCase();
  const mskuTrimmed = msku.trim();
  const listingTitleTrimmed = listingTitle.trim();
  const isValidFormat = /^[A-Z0-9]{10}$/.test(asinTrimmed);
  const isDuplicate = asinGroups.some(
    (a) => a.asin.toUpperCase() === asinTrimmed
  );
  const canSubmit =
    isValidFormat &&
    !isDuplicate &&
    !!mskuTrimmed &&
    !!listingTitleTrimmed &&
    !isSubmitting &&
    !!effectiveProductId;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`/product/${effectiveProductId}/listing`, {
        asin: asinTrimmed,
        msku: mskuTrimmed,
        listing_title: listingTitleTrimmed,
        created_by: createdBy || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setSubmitError("This ASIN is already linked to a product.");
      } else if (err.response?.status === 404) {
        setSubmitError("Product not found.");
      } else {
        setSubmitError(
          err.response?.data?.message || "An error occurred. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose()}
      closeOnDimmerClick={false}
      size="large"
    >
      <Modal.Header>Create a Product Listing</Modal.Header>
      <Modal.Content>
        {/* Product search (only when no productId prop provided) */}
        {needsSearch && !selectedProductId && (
          <>
            <Header as="h4" dividing>
              Search for a Product
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Search by product title or product ID to select a product.
            </p>
            <Form>
              <Form.Input
                placeholder="Search by title or product ID…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedProductId(undefined);
                  setSelectedProductTitle("");
                }}
                loading={isSearching}
                icon="search"
                iconPosition="left"
              />
            </Form>
            {searchResults.length > 0 && (
              <List
                divided
                selection
                style={{
                  marginTop: "0.5em",
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                {searchResults.map((result) => (
                  <List.Item
                    key={result.product_id}
                    onClick={() => {
                      setSelectedProductId(result.product_id);
                      setSelectedProductTitle(result.product_title);
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                    style={{ padding: "0.75em 1em", cursor: "pointer" }}
                  >
                    <List.Content>
                      <List.Header>
                        <Label size="tiny" horizontal>
                          ID {result.product_id}
                        </Label>
                        {result.product_title}
                      </List.Header>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
            {debouncedQuery.trim() &&
              !isSearching &&
              searchResults.length === 0 && (
                <Segment textAlign="center" style={{ color: "#999" }}>
                  No products found
                </Segment>
              )}
          </>
        )}

        {/* Selected Product */}
        {effectiveProductId && (
          <>
            <Header as="h4" dividing>
              Selected Product
              {needsSearch && (
                <Button
                  size="mini"
                  basic
                  floated="right"
                  style={{ marginTop: "-4px" }}
                  onClick={() => {
                    setSelectedProductId(undefined);
                    setSelectedProductTitle("");
                    setAsinGroups([]);
                    setAsin("");
                    setMsku("");
                    setListingTitle("");
                    setSubmitted(false);
                    setSuccess(false);
                  }}
                >
                  Change
                </Button>
              )}
            </Header>
            <Table definition compact>
              <Table.Body>
                <Table.Row>
                  <Table.Cell width={4}>Product ID</Table.Cell>
                  <Table.Cell>{effectiveProductId}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Product Title</Table.Cell>
                  <Table.Cell>{effectiveProductTitle}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>

            {/* Existing listings */}
            <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
              Amazon Listings
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Review currently linked listings before adding a new one.
            </p>

            {loadError && (
              <Message negative>
                <p>{loadError}</p>
              </Message>
            )}

            {loadingListings ? (
              <Segment style={{ minHeight: "60px" }}>
                <Dimmer active inverted>
                  <Loader size="small">Loading listings…</Loader>
                </Dimmer>
              </Segment>
            ) : asinGroups.length === 0 ? (
              <Segment textAlign="center" style={{ color: "#999" }}>
                No listings linked yet
              </Segment>
            ) : (
              <Table celled compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>ASIN</Table.HeaderCell>
                    <Table.HeaderCell>Offers</Table.HeaderCell>
                    <Table.HeaderCell>Title</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {asinGroups.map((group) => (
                    <Table.Row key={group.asin}>
                      <Table.Cell>{group.asin}</Table.Cell>
                      <Table.Cell>{getOffersLabel(group.listings)}</Table.Cell>
                      <Table.Cell>{getDisplayTitle(group.listings)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            {/* New Listing */}
            {!success && (
              <>
                <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
                  New Listing
                </Header>

                {submitError && (
                  <Message negative>
                    <p>{submitError}</p>
                  </Message>
                )}

                <Form>
                  <Form.Field required>
                    <label>Platform</label>
                    <Button.Group>
                      <Button type="button" primary>
                        Amazon
                      </Button>
                      <Button type="button" disabled title="Coming soon">
                        Walmart
                      </Button>
                    </Button.Group>
                  </Form.Field>
                  <Form.Field required>
                    <label>ASIN</label>
                    <Form.Input
                      placeholder="Enter ASIN"
                      value={asin}
                      onChange={(e) => {
                        setAsin(e.target.value);
                        setSubmitError(null);
                      }}
                      maxLength={10}
                      disabled={isSubmitting}
                    />
                    {submitted && !asinTrimmed && (
                      <Label basic color="red" pointing>
                        ASIN is required
                      </Label>
                    )}
                    {submitted && asinTrimmed && !isValidFormat && (
                      <Label basic color="red" pointing>
                        ASIN must be exactly 10 alphanumeric characters
                      </Label>
                    )}
                    {asinTrimmed && isValidFormat && isDuplicate && (
                      <Label basic color="red" pointing>
                        This ASIN is already linked to this product.
                      </Label>
                    )}
                  </Form.Field>
                  <Form.Field required>
                    <label>MSKU</label>
                    <Form.Input
                      placeholder="Enter MSKU"
                      value={msku}
                      onChange={(e) => {
                        setMsku(e.target.value);
                        setSubmitError(null);
                      }}
                      disabled={isSubmitting}
                    />
                    {submitted && !mskuTrimmed && (
                      <Label basic color="red" pointing>
                        MSKU is required
                      </Label>
                    )}
                  </Form.Field>
                  <Form.Field required>
                    <label>Listing Title</label>
                    <Form.Input
                      placeholder="Enter Listing Title"
                      value={listingTitle}
                      onChange={(e) => {
                        setListingTitle(e.target.value);
                        setSubmitError(null);
                      }}
                      disabled={isSubmitting}
                    />
                    {submitted && !listingTitleTrimmed && (
                      <Label basic color="red" pointing>
                        Listing Title is required
                      </Label>
                    )}
                  </Form.Field>
                  <Form.Field>
                    <label>Created On</label>
                    <Form.Input disabled value={createdOn} readOnly />
                  </Form.Field>
                  <Form.Field>
                    <label>Created By</label>
                    <Form.Input disabled value={createdBy} readOnly />
                  </Form.Field>
                </Form>
              </>
            )}

            {success && (
              <Message positive>
                <Message.Header>Listing Created Successfully!</Message.Header>
                <p>
                  ASIN <strong>{asinTrimmed}</strong> has been linked to this
                  product.
                </p>
              </Message>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(success)}>
          {success ? "Close" : "Cancel"}
        </Button>
        {effectiveProductId && !success && (
          <Button
            primary
            onClick={handleSubmit}
            disabled={!canSubmit || loadingListings}
            loading={isSubmitting}
          >
            Add Listing
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
